<!-- src/lib/components/filters/FilterPanel.svelte -->

<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { submitFilters } from '$lib/api/filters';
  import { submitNeighborSamples } from '$lib/api/neighbors';
  import { neighborSamples } from '$lib/stores/neighbors';
  import { sidebarOpen } from '$lib/stores/ui';
  import { queryRunning } from '$lib/stores/selection';
  import SampleCompanySelector from '$lib/components/Neighbors/SampleCompanySelector.svelte';
  import ApprenticeshipFilter from '$lib/components/ApprenticeshipFilter.svelte';
  import HiringFilter from '$lib/components/HiringFilter.svelte';
  import TechnologyFilter from '$lib/components/TechnologyFilter.svelte';
  import CertificationFilter from '$lib/components/CertificationFilter.svelte';
  import SalesModelFilter from '$lib/components/SalesModelFilter.svelte';
  import GroupFilter from '$lib/components/GroupFilter.svelte';
  import FleetFilter from '$lib/components/FleetFilter.svelte';
  import CapitalFilters from '$lib/components/CapitalFilters.svelte';
  import ContactFilters from '$lib/components/ContactFilters.svelte';
  import FoundingFilters from '$lib/components/FoundingFilters.svelte';
  import LegalFormFilters from '$lib/components/LegalFormFilters.svelte';
  import DescriptionFilter from '$lib/components/DescriptionFilter.svelte';
  import RegionFilters from '$lib/components/RegionFilters.svelte';
  import SelectedDatapools from '$lib/components/SelectedDatapools.svelte';
  import RoleFilters from '$lib/components/RoleFilter/RoleFilter.svelte';
  import SizeFilters from '$lib/components/SizeFilters.svelte';
  import SignalsFilters from '$lib/components/SignalsFilters.svelte';

  /**
   * Whether this panel should fill the height it is given.
   *
   * True in the desktop sidebar, which is a fixed-height column: the scroll
   * area takes the slack and the search button pins to the panel's foot, where
   * it stays put while the filters scroll under it.
   *
   * False in the mobile drawer, which is `inset-y-0` and scrolls itself. There
   * `h-full` resolved to the whole viewport however little was in the panel,
   * and `flex-1` pushed the search button to the bottom of that — so a short
   * set of filters left a screen's worth of empty rail above the button, and
   * the drawer scrolled further than its contents needed.
   */
  export let fill = true;

  /**
   * Whether this is similarity mode's panel.
   *
   * # One panel, because the other one drifted
   *
   * This was two components. NeighborsFilters imported the same filter
   * controls, stacked them in its own order, and was left alone while classic
   * was being worked out — the plan being to copy the result across in one pass
   * once it settled. By the time it had, similarity mode was missing the
   * description filter, the signals filter, apprenticeships and the whole
   * technology filter, had contact channels behind the fold where classic has
   * them in front of it, still carried the worded "Mehr Filter" buttons classic
   * replaced with a chevron, and had a hardcoded English "Search" on a button
   * the rest of the app translates.
   *
   * None of that was decided. It is what two files holding one layout comes to,
   * and copying the layout across a second time would only restart the clock.
   * So the panel is one component and the two modes differ where they actually
   * differ, which is twice:
   *
   *   - what sits above the filters: classic picks industries, similarity picks
   *     example companies
   *   - what the button does, and when it is allowed to
   *
   * Everything between those is shared, so a filter added to one is in the
   * other by construction rather than by remembering.
   */
  export let neighbours = false;

  let showMoreFilters = false;
</script>

<!--
  No assistant button here.

  It stood at the top of the panel, above the filters, which put a second way
  to build a search over the first one — and it was the largest control in a
  column whose job is narrowing, before any of the narrowing began.

  The assistant is not gone with it: `AIChatOverlay` carries a launcher of its
  own, and it renders on exactly the screen this panel appears on. Two entry
  points to one conversation, one of them occupying the top of the sidebar, was
  one more than the screen needed.
-->
<div class="flex w-full flex-col px-2 py-3 {fill ? 'h-full' : ''}">
  <!--
    One surface, not five.

    Each group in here was a white card with its own border and radius, laid on
    the rail’s tint: four raised patches inside a panel that is already a
    bordered, rounded panel of its own. Two levels of the same chrome nested
    inside each other, and the sidebar came out looking blotched rather than
    built.

    The groups lose their fill and keep their dividers. What stays white is the
    controls — inputs, the search button, the assistant — which is the right
    way round: the panel recedes and the things you operate sit forward on it.

    # The dividers had to get darker to survive it

    Everything in here separated with `--border-subtle`, which is #f3f4f6 — a
    hairline chosen to be nearly invisible against white, which is what it sat
    on. The rail is #f4f4f6. One step apart is no steps, so the moment the
    cards went, every line in the panel went with them.

    They use `--border-default` now, and the gaps between groups became lines
    too. A group separated only by 10px of space was legible while each one was
    a white card with an edge; once they are all the same surface, spacing
    alone does not say where one filter ends and the next begins.

    This is the same collision that took out the disabled search button, and it
    is worth expecting again: the tokens tuned against white — `subtle` and
    `sunken` — are the ones with no room left on a tinted surface.

    # Darkened once, and then given the job back

    They went to `strong` for a while, because an expanded filter is a dozen
    rows of its own controls and the line between one and the next was a single
    hairline among a screenful of them. That was the line doing a job it is not
    shaped for: a heavier rule separates two rows, and what needed separating
    was two blocks.

    The open filter has its own surface now — see `surface-well` — so the block
    says where it begins and ends without help. The dividers are back to
    `default` and are only doing what is left, which is separating the closed
    rows from each other. Those are single lines of text on the rail with
    nothing else to tell them apart, so they cannot go to `subtle`: that is
    #f3f4f6 against a #f4f4f6 rail, which is no line at all.

    # Both modes, now

    This was classic only, with NeighborsFilters holding a second copy of the
    same layout, on the plan that classic would be worked out first and copied
    across in one pass. That pass is this file: see the `neighbours` prop for
    what the second copy had quietly lost in the meantime.
  -->
  <div class="space-y-3 overflow-x-hidden {fill ? 'flex-1 overflow-y-auto' : ''}">
    <!--
      What the selection is drawn from, first because everything below narrows
      within it. Industries in classic; in similarity mode the example
      companies the neighbours are found from, which is the same slot asking
      the same question a different way.
    -->
    {#if neighbours}
      <SampleCompanySelector />
    {:else}
      <SelectedDatapools />
    {/if}

    <!-- Filters card -->
    <div class="divide-y divide-hairline">
      <!--
        What the company does, described. First in the card because it is the
        broadest cut: it decides which companies are candidates at all, where
        region and size narrow whoever is left.
      -->
      <DescriptionFilter />
      <RegionFilters />
      <SizeFilters />

      <!--
        Signals and channels close the always-visible block.

        Signals was its own region below the fold, which made it look like a
        different kind of control than it is — it narrows the company list like
        everything else here, and standing apart it read as an afterthought.
        Inside the block it is one more filter, which is all it is.

        Channels last, because it is the only one that asks how to reach a
        company rather than what the company is.
      -->
      <SignalsFilters />
      <ContactFilters />

      <!-- More filters toggle -->
      <!-- Collapsible filters -->
      {#if showMoreFilters}
        <FoundingFilters />
        <CapitalFilters />
        <LegalFormFilters />
        <SalesModelFilter />
        <GroupFilter />
        <FleetFilter />
        <ApprenticeshipFilter />
        <HiringFilter />
        <TechnologyFilter />
        <CertificationFilter />
      {/if}

      <!--
        One chevron, no words.

        It was two buttons reading "Mehr Filter" and "Weniger Filter", each
        with its own arrow, and at the width of the rail a labelled row looks
        like another filter — one that cannot be ticked and has no content. The
        arrow alone is the same affordance without pretending to be a section.

        The label lives on the button rather than in it, so a screen reader
        still hears which way it goes.
      -->
      <div class="flex justify-center py-1">
        <button
          type="button"
          on:click={() => (showMoreFilters = !showMoreFilters)}
          aria-expanded={showMoreFilters}
          aria-label={showMoreFilters ? $_('filters.showLess') : $_('filters.showMore')}
          title={showMoreFilters ? $_('filters.showLess') : $_('filters.showMore')}
          class="rounded-control p-1.5 text-gray-400 transition-colors duration-200 hover:bg-surface-well hover:text-gray-700"
        >
          <svg
            class="h-4 w-4 transition-transform duration-200 {showMoreFilters ? 'rotate-180' : ''}"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
    </div>

    <!--
      Signals and channels sit below the fold rather than inside it.

      Both were in the collapsed half — channels at the very bottom of it,
      signals just outside — which put two filters people reach for often
      behind a click, and left the arrow buried between them. Out here they are
      always available and the arrow ends the block it belongs to.
    -->
    <!--
      Roles are their own region, and the last one.

      Everything above narrows which companies are in the selection. This asks
      who inside them to address, which is a different question and the one
      asked last. Space says that; a rule would have filed it as one more
      filter.
    -->
    <RoleFilters />
  </div>

  <!-- Submit Button -->
  <div class="pt-2">
    <button
      class="search-btn h-10 w-full rounded-control px-4 text-sm font-semibold text-white transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed"
      on:click={() => {
        if (!$queryRunning) {
          sidebarOpen.set(false);
          if (neighbours) {
            submitNeighborSamples();
          } else {
            submitFilters();
          }
        }
      }}
      disabled={$queryRunning || (neighbours && $neighborSamples.length < 1)}
    >
      <span class="flex items-center justify-center gap-2">
        {#if $queryRunning}
          <svg class="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        {:else}
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        {/if}
        {$_('buttons.search')}
      </span>
    </button>
  </div>
</div>

<style>
  /*
   * This was one of two local gradients — the other purple-to-pink — written
   * past the shared button styles, so the sidebar's two most prominent
   * controls were the only ones in the product that looked like this. The
   * other was the assistant, which has since left the panel entirely.
   *
   * What remains is an ordinary button in the accent.
   */
  .search-btn {
    background: rgb(var(--accent-600));
  }
  .search-btn:hover:not(:disabled) {
    background: rgb(var(--accent-700));
  }
  /*
   * Not `--surface-sunken`. That is #f3f4f6 and the rail this button sits on
   * is #f4f4f6, so a disabled button filled with it had no edge at all — grey
   * text floating on the panel with no button under it. `--border-default` is
   * the same value the rest of the app disables with (`.btn:disabled` and
   * friends use gray-200, which is this triple), so the button keeps a shape
   * and matches every other dead control.
   */
  .search-btn:disabled {
    background: rgb(var(--border-default));
    color: rgb(107 114 128);
  }
</style>
